import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import GQL from '@lib/cms/gql';
import FlexibleDataOptimized from '@lib/cms/flexibleDataOptimized';
import { SCHOOLS_QUERY } from '@lib/fragments';
import { useDebounce } from '@lib/hooks';

import Container from '@layout/container';
import Grid from '@layout/grid';
import Pagination from '@components/pagination/component';
import FlexiblePage from '@templates/FlexiblePage';
import Select from '@components/select';
import Spinner from '@components/spinner';
import ArticleCard from '@components/article-card';

import {
  search_bar,
  state_sort,
  type_sort,
  search,
  card_outer,
  spinner,
  zero_state
} from './styles.module.scss';

const PAGESIZE = 15;

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment ?? null;
  const data = await FlexibleDataOptimized.fetch('schools', preview, previewEnv);

  return {
    props: { landingData: data, preview, previewEnv }
  };
}

const SchoolLandingPage = ({ landingData, preview, previewEnv }) => {
  const router = useRouter();
  const [pageNum, setPageNum] = useState(0);
  const [stateValue, setStateValue] = useState('all');
  const [typeValue, setTypeValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(true);

  useEffect(() => {
    if (searchTerm.length < 2 && pageNum === 0 && !shouldRefetch) return;

    (async () => {
      setLoading(true);

      const where = {
        ...(searchTerm !== '' && { name_contains: searchTerm }),
        ...(stateValue !== 'all' && { state_contains: stateValue }),
        ...(typeValue !== 'all' && { type_contains: typeValue })
      };

      const data = await GQL.fetch(
        SCHOOLS_QUERY,
        { limit: PAGESIZE, skip: pageNum * PAGESIZE, where },
        preview,
        previewEnv
      );

      if (data.errors) throw new Error(`Error fetching school data: ${data.errors[0].message}`);

      const schoolsData = data?.data;

      setData(schoolsData);
      setLoading(false);
      setShouldRefetch(searchTerm.length >= 2);
    })();
  }, [pageNum, stateValue, typeValue, debouncedSearch]);

  useEffect(() => {
    if (router?.isReady) {
      const page = router?.query?.page;
      setPageNum(parseInt(page) - 1 || 0);
    }
  }, [router.isReady, router?.query?.page]);

  const handleStateSelectChange = (newStateValue) => {
    setShouldRefetch(true);
    setStateValue(newStateValue);
  };
  const handleTypeSelectChange = (newTypeValue) => {
    setShouldRefetch(true);
    setTypeValue(newTypeValue);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNum(0);
  };

  const schools = data?.schools?.items || [];

  const total = data?.schools?.total;

  return (
    <FlexiblePage data={landingData} preview={preview}>
      {landingData && (
        <main>
          <Container spacing="large" gridOptions={{ layout: 'single' }}>
            <div className={search_bar}>
              <div className={state_sort}>
                <Select
                  options={[
                    {
                      label: 'All States',
                      value: 'all'
                    },
                    {
                      label: 'Kentucky',
                      value: 'KY'
                    },
                    {
                      label: 'Indiana',
                      value: 'IN'
                    },
                    {
                      label: 'Ohio',
                      value: 'OH'
                    }
                  ]}
                  onChange={handleStateSelectChange}
                  selected={stateValue}
                />
              </div>
              <div className={type_sort}>
                <Select
                  options={[
                    {
                      label: 'All Schools',
                      value: 'all'
                    },
                    {
                      label: 'Public',
                      value: 'Public'
                    },
                    {
                      label: 'Private',
                      value: 'Private'
                    }
                  ]}
                  onChange={handleTypeSelectChange}
                  selected={typeValue}
                />
              </div>
              <div className={search}>
                <input
                  tabIndex="0"
                  value={searchTerm}
                  onChange={handleInputChange}
                  placeholder="Search by school name"
                />
              </div>
            </div>

            {loading && (
              <div className={spinner}>
                <Spinner />
              </div>
            )}
            {total === 0 && (
              <div className={zero_state}>
                {stateValue !== 'all' || typeValue !== 'all'
                  ? 'No schools were found that match these specifications. Try updating your filters.'
                  : 'No schools meet your search criteria .'}
              </div>
            )}
            {!loading && schools && (
              <Grid gutter="20" layout="3">
                {schools.map((school, i) => (
                  <div className={card_outer} key={i}>
                    <ArticleCard
                      description={
                        school?.excerpt || school?.introductionText || school?.summaryText
                      }
                      eyebrow={school.type.toUpperCase()}
                      image={
                        school?.featuredImage || {
                          height: 960,
                          url: 'https://images.ctfassets.net/wzzep3ntdgx4/myGsn15lbSM9jEfIGsoNk/3780518a9b3fea069f01f5b3fb06ed78/IMG_0476.jpg',
                          width: 1440
                        }
                      }
                      link={{
                        aria: 'Explore this school',
                        icon: true,
                        label: school?.linkLabel,
                        url: school?.linkUrl
                      }}
                      theme="community"
                      title={school?.title}
                    />
                  </div>
                ))}
              </Grid>
            )}
          </Container>
          {!loading && total > PAGESIZE && (
            <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
              <Pagination
                items={Array.from(Array(total || 0).keys())}
                per={PAGESIZE}
                currentPage={pageNum}
              />
            </Container>
          )}
        </main>
      )}
    </FlexiblePage>
  );
};

export default SchoolLandingPage;
